import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GeneralSettingsService } from '../../../general-settings/general-settings.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import ISO6391 from 'iso-639-1';
import { MatSnackBar } from '@angular/material';

@Component({
  selector: 'translatable-text-translations-dialog',
  templateUrl: './translatable-text-translations-dialog.component.html',
  styleUrls: ['./translatable-text-translations-dialog.component.scss']
})
export class TranslatableTextTranslationsDialogComponent implements OnInit, OnDestroy {
  formGroup: FormGroup;
  dialogTitle: string;
  languages: any[] = [];
  private _unsubscribeAll: Subject<any> = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { formGroup: FormGroup, title?: string },
    public dialogRef: MatDialogRef<TranslatableTextTranslationsDialogComponent>,
    public generalSettingsService: GeneralSettingsService,
    private fuseSplashScreen: FuseSplashScreenService,
    private snackBar: MatSnackBar
  ) {
    this.formGroup = data.formGroup;
    this.dialogTitle = data.title || 'Manage Translations';
  }

  ngOnInit() {
    this.generalSettingsService.getLanguages().then(langs => {
      this.languages = Array.isArray(langs) ? langs : [];
    });
    
    this.generalSettingsService.onLanguagesChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(languages => {
        if (Array.isArray(languages)) {
          this.languages = languages;
        }
      });
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  get translationsControl() {
    return this.formGroup.get('translations');
  }

  async autoTranslateMissing() {
    const originalText = this.formGroup.get('original').value;
    if (!originalText) return;

    this.fuseSplashScreen.show();

    const currentTranslations = this.translationsControl.value || {};
    
    const missingLanguages = this.languages.filter(lang => {
      const trans = currentTranslations[lang.code];
      return !trans || trans.trim() === '';
    });

    let hasError = false;
    let errorLanguages = [];
    let isRateLimit = false;

    for (const lang of missingLanguages) {
      const isoCode = ISO6391.getCode(lang.name) || lang.code;
      try {
        const res: any = await this.generalSettingsService.translatingFromGoogle([originalText], isoCode).toPromise();
        if (res && res.length > 0 && res[0]) {
          currentTranslations[lang.code] = res[0];
        }
      } catch (err) {
        console.error('Error translating to ' + lang.name, err);
        hasError = true;
        errorLanguages.push(lang.name);
        if (err && err.status === 403) {
          isRateLimit = true;
        }
      }
    }

    if (hasError) {
      const msg = isRateLimit 
        ? "Translation failed: rate limit exceeded, please try again shortly."
        : `Translation failed for: ${errorLanguages.join(', ')}. Please try again.`;
      
      this.snackBar.open(msg, "ERROR", {
        verticalPosition: "top",
        duration: 5000,
      });
    }

    this.translationsControl.setValue(currentTranslations);
    this.translationsControl.updateValueAndValidity();
    this.fuseSplashScreen.hide();
  }

  async retranslateLanguage(langCode: string, langName: string) {
    const originalText = this.formGroup.get('original').value;
    if (!originalText) return;

    this.fuseSplashScreen.show();
    
    const currentTranslations = this.translationsControl.value || {};
    const isoCode = ISO6391.getCode(langName) || langCode;
    
    try {
      const res: any = await this.generalSettingsService.translatingFromGoogle([originalText], isoCode).toPromise();
      if (res && res.length > 0 && res[0]) {
        currentTranslations[langCode] = res[0];
        this.translationsControl.setValue(currentTranslations);
        this.translationsControl.updateValueAndValidity();
      }
    } catch (err) {
      console.error('Error re-translating ' + langName, err);
      const isRateLimit = err && err.status === 403;
      const msg = isRateLimit 
        ? "Translation failed: rate limit exceeded, please try again shortly."
        : "Translation failed, please try again.";

      this.snackBar.open(msg, "ERROR", {
        verticalPosition: "top",
        duration: 3000,
      });
    }
    this.fuseSplashScreen.hide();
  }

  updateTranslation(langCode: string, newValue: string) {
    const currentTranslations = this.translationsControl.value || {};
    currentTranslations[langCode] = newValue;
    this.translationsControl.setValue(currentTranslations);
    this.translationsControl.updateValueAndValidity();
  }
}
